// Fix for the "Choose options" quick-view popup on Studd Muffyn collection pages.
//
// THREE SYMPTOMS, ONE CAUSE
// The modal injects the product HTML after page load, but none of the theme's
// JS components are initialised for that injected content. So:
//
//   1. Clicking a pack tile checks the radio but never updates the hidden
//      <select name="id">, which is what the form actually submits.
//      => selecting "PACK OF 2" and hitting ADD TO CART added the SINGLE.
//   2. The price/discount in the modal header never changes.
//   3. The inventory counter renders its label ("Availability"), its markup and
//      its data JSON, but nothing populates the message or sets
//      data-stock-category — and without that attribute the theme's CSS leaves
//      --message-color as `transparent`, so even the text is invisible.
//
// Note on the transition: .inventory-counter__message has
// `transition: color .5s .2s`. On freshly injected nodes the colour transition
// does not repaint when --message-color changes, leaving the text transparent
// even after the attribute is set. Neutralising the transition fixes it.
//
// USAGE IN THE THEME: call initQuickViewFix(modalEl) whenever the quick-view
// modal finishes rendering its content. The MutationObserver at the bottom does
// that automatically if you'd rather not hunt for the modal's open hook.

(function () {
  const rupees = (n) =>
    '₹' + new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(n);

  const toNumber = (s) => Number(String(s).replace(/[^\d.]/g, ''));

  function initQuickViewFix(modal) {
    if (!modal || modal.__qvFixed) return;

    const select = modal.querySelector('select[name="id"]');
    const radios = [...modal.querySelectorAll('input[name="pack_variant_id"]')];
    if (!select || !radios.length) return;

    modal.__qvFixed = true;

    const counter = modal.querySelector('.inventory-counter');
    const message = counter && counter.querySelector('.inventory-counter__message');
    const messageText = counter && counter.querySelector('.inventory-counter__message-text');
    const bar = counter && counter.querySelector('.inventory-counter__bar-progress');

    // see note above — without this the message stays transparent
    if (message) message.style.transition = 'none';
    if (bar) bar.style.transition = 'none';

    let inventory = {};
    const json = modal.querySelector('[data-product-inventory-json]');
    if (json) {
      try { inventory = (JSON.parse(json.textContent) || {}).inventory || {}; } catch (e) { /* leave empty */ }
    }

    const lowThreshold = counter ? Number(counter.dataset.lowInventoryThreshold || 10) : 10;
    const countdownMax = counter ? Number(counter.dataset.stockCountdownMax || 100) : 100;

    const sync = () => {
      const checked = modal.querySelector('input[name="pack_variant_id"]:checked');
      if (!checked) return;

      // 1. the part that actually matters — point the cart form at the right variant
      select.value = checked.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));

      // 2. mirror the tile's own prices into the modal header
      const label = checked.closest('label');
      const price = toNumber(label.querySelector('.pack-price').textContent);
      const compareEl = label.querySelector('.pack-compare');
      const compare = compareEl ? toNumber(compareEl.textContent) : 0;

      modal.querySelectorAll('.product__price').forEach((block) => {
        const priceEl = block.querySelector('[data-price]');
        const compareOut = block.querySelector('[data-compare-price]');
        const discountWrap = block.querySelector('[data-discount-display]');
        const discountEl = discountWrap && discountWrap.querySelector('.displayed-discount');

        if (priceEl) priceEl.textContent = rupees(price);
        if (compareOut) {
          compareOut.textContent = rupees(compare);
          compareOut.style.display = compare > price ? '' : 'none';
        }
        if (discountWrap) {
          discountWrap.style.display = compare > price ? '' : 'none';
          if (discountEl && compare > price) {
            discountEl.textContent = Math.round(((compare - price) / compare) * 100) + '% off';
          }
        }
      });

      // 3. populate the inventory counter
      if (counter) {
        const info = inventory[checked.value];
        let category = 'unavailable';
        let text = counter.dataset.unavailableText || 'Unavailable';
        let qty = 0;

        if (info) {
          qty = info.inventory_quantity;
          text = info.inventory_message || '';
          category = qty <= 0
            ? (info.inventory_policy === 'continue' ? 'sufficient' : 'empty')
            : (qty <= lowThreshold ? 'low' : 'sufficient');
        }

        if (messageText) messageText.textContent = text;
        if (bar) bar.style.width = Math.max(0, Math.min(100, (qty / countdownMax) * 100)) + '%';

        counter.removeAttribute('data-stock-category');
        void counter.offsetWidth;
        counter.setAttribute('data-stock-category', category);
      }
    };

    radios.forEach((r) => r.addEventListener('change', sync));
    sync(); // correct the initial render too
  }

  window.initQuickViewFix = initQuickViewFix;

  // Auto-wire: run whenever a quick-view modal becomes visible.
  new MutationObserver(() => {
    document.querySelectorAll('.quick-product-modal.is-visible')
      .forEach(initQuickViewFix);
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
})();
